import { useState, useEffect } from "react";
import { Save } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../components/Toast";
import Card from "../components/Card";
import type { QueueSettings } from "../types";

const defaults: QueueSettings = {
  minDelayMs: 15000,
  maxDelayMs: 45000,
  recipientCooldownSec: 300,
  hourlyLimit: 20,
  dailyLimit: 80,
  maxAttempts: 1,
};

export default function SettingsPage() {
  const { toast } = useToast();
  const [settings, setSettings] = useState<QueueSettings>(defaults);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/settings/queue");
        if (res.data.ok) {
          setSettings({
            minDelayMs: res.data.minDelayMs ?? defaults.minDelayMs,
            maxDelayMs: res.data.maxDelayMs ?? defaults.maxDelayMs,
            recipientCooldownSec: res.data.recipientCooldownSec ?? defaults.recipientCooldownSec,
            hourlyLimit: res.data.hourlyLimit ?? defaults.hourlyLimit,
            dailyLimit: res.data.dailyLimit ?? defaults.dailyLimit,
            maxAttempts: res.data.maxAttempts ?? defaults.maxAttempts,
          });
        }
      } catch {}
    })();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post("/settings/queue", settings);
      if (res.data.ok) toast("Settings saved. Takes effect on next worker start.", "success");
      else toast(res.data.error || "Error", "error");
    } catch (err: any) { toast(err.response?.data?.error || "Error", "error"); }
    finally { setSaving(false); }
  };

  const update = (key: keyof QueueSettings, value: string) => {
    setSettings((s) => ({ ...s, [key]: parseInt(value, 10) || 0 }));
  };

  const fields: { key: keyof QueueSettings; label: string; min: number; max: number }[] = [
    { key: "minDelayMs", label: "Min delay per message (ms)", min: 0, max: 60000 },
    { key: "maxDelayMs", label: "Max delay per message (ms)", min: 0, max: 60000 },
    { key: "recipientCooldownSec", label: "Recipient cooldown (sec)", min: 0, max: 86400 },
    { key: "hourlyLimit", label: "Hourly limit (msg/hr)", min: 1, max: 1000 },
    { key: "dailyLimit", label: "Daily limit (msg/day)", min: 1, max: 10000 },
    { key: "maxAttempts", label: "Max attempts per recipient", min: 1, max: 10 },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-zinc-400 text-sm mt-1">Configure sending behavior</p>
      </div>

      <Card title="Send Settings">
        <div className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
          Usa questo sistema solo per contatti con opt-in esplicito e messaggi attesi. Con una sessione WhatsApp Web non esiste un settaggio veramente “safe” per outbound continuativo: questi limiti servono solo a ridurre il rischio, non a bypassare i controlli. Per volumi stabili la soluzione corretta resta la WhatsApp Business Platform ufficiale.
        </div>
        <form onSubmit={handleSave}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {fields.map(({ key, label, min, max }) => (
              <div key={key}>
                <label className="text-sm text-zinc-400 mb-1 block font-medium">{label}</label>
                <input
                  type="number"
                  value={settings[key]}
                  onChange={(e) => update(key, e.target.value)}
                  min={min}
                  max={max}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-fuchsia-500 transition-colors"
                />
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-3">
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-600 to-orange-500 text-white font-semibold hover:from-fuchsia-500 hover:to-orange-400 disabled:opacity-40 transition-all">
              <Save size={16} />
              {saving ? "Saving..." : "Save"}
            </button>
            <span className="text-xs text-zinc-500">Takes effect on next worker start.</span>
          </div>
        </form>
      </Card>
    </div>
  );
}
